import {Component, effect, inject, model, ModelSignal} from '@angular/core';
import {
  CSV_EXPORT_ACTION,
  CSV_IMPORT_ACTION,
  DELETE_ACTION,
  DialogService,
  ITableAction,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
  ToolbarStore,
} from 'frontier/browserkit';
import {EControlActions, EsvgFiles, IControlObject, IFilter, INITIAL_STATE_TOKEN} from 'frontier/nucleus';
import {GridOptions, RowNode} from 'ag-grid-community';
import {
  DatePickerDialogComponent
} from 'frontier/browserkit/src/lib/dialogs/date-picker-dialog/date-picker-dialog.component';
import {concatMap, Observable, of} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {TabsService} from 'frontier/browserkit/src/lib/components/home-without-gl/tabs/tabs.service';
import {ItemPriceHistoryViewService} from 'src/app/api/generated';
import AppControlGuids from '../../../api/ControlGuids';
import {ITab} from 'frontier/browserkit/src/lib/components/home-without-gl/tabs/tab.interface';

@Component({
  selector: 'app-tariff-changes',
  templateUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  standalone: true,
  imports: [TableControlModule],
  providers: [{provide: INITIAL_STATE_TOKEN, useValue: {filter: null}}],
})
export class TariffChangesComponent extends TableControlComponent {
  protected readonly EControlActions: typeof EControlActions = EControlActions;
  private _itemPriceHistoryService: ItemPriceHistoryViewService = inject(ItemPriceHistoryViewService);
  private _toolbarStore: ToolbarStore = inject(ToolbarStore);
  private _tabsService: TabsService = inject(TabsService);
  private _dialogService: DialogService = inject(DialogService);

  override GUID: string = AppControlGuids.TItemPriceHistoryView;
  override gridOptions: GridOptions = {
    ...SharedGridOptions,
    rowSelection: 'multiple',
  };
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    DELETE_ACTION,
    {
      displayName: 'Tarifänderung',
      controlAction: EControlActions.tariffChange,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.settings,
      action: () => this.tariffChanges(),
    },
    {
      displayName: 'Preise Rückwirkend Anwenden',
      controlAction: EControlActions.recalculateItemPriceHistory,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.refresh,
      action: () => this.recalculateItemPriceHistory(),
    },
    {
      displayName: 'Tarifoptionen',
      controlAction: EControlActions.tariffOption,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.settings,
      action: () => this.openTariffOptions(),
    },
    CSV_EXPORT_ACTION,
    CSV_IMPORT_ACTION,
  ]);

  constructor() {
    super();
    effect((): void => {
      const toolbarFilterDto: IFilter = this._toolbarStore.toolbarFilterDto();
      if (toolbarFilterDto) {
        this.patchFilter({...toolbarFilterDto})
      }
    }, {allowSignalWrites: true});
  }

  private tariffChanges(): Observable<any> {
    return this._dialogService.dialog.open(DatePickerDialogComponent, {
      autoFocus: 'dialog'
    }).afterClosed().pipe(
      filter((date: any): boolean => date != null),
      concatMap((date: any): Observable<boolean> => {
        return this._itemPriceHistoryService.itemPriceHistoryViewUpdateItemPriceHistory({
          validfrom: date,
          values: this.gridApi.getSelectedNodes().map((r: RowNode): IControlObject => r.data.apiRow.obj),
        })
      }),
      tap(() => {
        return this.changeAndFetchInstance();
      })
    );
  }

  private recalculateItemPriceHistory(): Observable<any> {
    return this._itemPriceHistoryService.itemPriceHistoryViewRecalculateItemPriceHistory().pipe(
      tap((): void => {
        this.feedbackService.setNotification(
          'Die Preisliste wurde erfolgreich auf die offenen Fahrten angewendet.');
      })
    )
  }

  private openTariffOptions(): Observable<null> {
    const existing: ITab = this._tabsService.tabs().find((tab: ITab): boolean => tab.tabId === 'tariff-options');
    if (existing) {
      this._tabsService.selectTabById('Tarifoptionen');
    } else {
      this._tabsService.openTab('Tarifoptionen', 'TariffOptionsComponent', 'tariff-options');
    }
    return of(null);
  }
}
